'use client';
import { useState } from 'react';
import { useResource } from '@/lib/use-resource';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import type { PeerGroupTrendsResponse } from '@/app/api/peer-group-trends/route';

const GROUP_COLORS: Record<string, string> = {
  technology: '#3b82f6',
  financial:  '#10b981',
  energy:     '#f97316',
  utility:    '#eab308',
  reit:       '#a855f7',
  insurance:  '#ec4899',
  standard:   '#94a3b8',
};

const GROUP_LABELS: Record<string, string> = {
  technology: 'Technology',
  financial:  'Financial',
  energy:     'Energy',
  utility:    'Utility',
  reit:       'REIT',
  insurance:  'Insurance',
  standard:   'Standard',
};

const ALL_GROUPS = ['technology', 'financial', 'energy', 'utility', 'reit', 'insurance', 'standard'] as const;
type Group = typeof ALL_GROUPS[number];
type Metric = 'q' | 'v';

function fmtDate(d: string) {
  // "2017-01-31" → "Jan 17"
  const [y, m] = d.split('-');
  const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m)]} '${y.slice(2)}`;
}

// Merge all groups into recharts-friendly flat array
function buildChartData(
  groups: PeerGroupTrendsResponse['groups'],
  dates: string[],
  activeGroups: Set<string>,
  metric: Metric,
) {
  return dates.map(date => {
    const row: Record<string, number | string> = { date, label: fmtDate(date) };
    for (const pg of ALL_GROUPS) {
      if (!activeGroups.has(pg)) continue;
      const pt = groups[pg]?.find(p => p.date === date);
      if (pt) {
        const mean = metric === 'q' ? pt.qMean : pt.vMean;
        const s = metric === 'q' ? pt.qStd : pt.vStd;
        row[`${pg}_mean`] = mean;
        row[`${pg}_hi`] = mean + s;
        row[`${pg}_lo`] = mean - s;
      }
    }
    return row;
  });
}

// Custom tooltip
function CustomTooltip({ active, payload, label, metric }: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
  metric: Metric;
}) {
  if (!active || !payload?.length) return null;
  const means = payload.filter(p => p.dataKey.endsWith('_mean'));
  return (
    <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-3 text-[10px] shadow-xl min-w-[160px]">
      <div className="text-[#64748b] mb-2 font-mono">{label}</div>
      {means.map(p => {
        const pg = p.dataKey.replace('_mean', '');
        return (
          <div key={pg} className="flex items-center justify-between gap-3 mb-0.5">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: GROUP_COLORS[pg] }} />
              <span className="text-[#94a3b8]">{GROUP_LABELS[pg]}</span>
            </div>
            <span className="tabular-nums font-mono text-white">
              {p.value.toFixed(3)}
            </span>
          </div>
        );
      })}
      <div className="text-[#475569] mt-1">{metric === 'q' ? 'Quality' : 'Valuation'} score (mean ± 1σ)</div>
    </div>
  );
}

export function PeerGroupTrends() {
  const { data, loading } = useResource<PeerGroupTrendsResponse>('/api/peer-group-trends');
  const [metric, setMetric] = useState<Metric>('q');
  const [activeGroups, setActiveGroups] = useState<Set<Group>>(new Set(ALL_GROUPS));
  const [showBands, setShowBands] = useState(false);

  function toggleGroup(pg: Group) {
    setActiveGroups(prev => {
      const next = new Set(prev);
      if (next.has(pg)) {
        if (next.size > 1) next.delete(pg);
      } else {
        next.add(pg);
      }
      return next;
    });
  }

  if (loading || !data) {
    return (
      <div className="py-12 text-center text-[#64748b] text-[12px]">
        Loading peer group trends…
      </div>
    );
  }

  const chartData = buildChartData(data.groups, data.dates, activeGroups, metric);

  return (
    <div>
      <div className="mb-4">
        <p className="text-[11px] text-[#64748b] leading-relaxed max-w-3xl">
          Mean {metric === 'q' ? 'Quality' : 'Valuation'} score for each of the 7 peer groups, monthly from 2017 to {data.asOf}.
          Shaded bands show ±1σ across names in the group.
          {metric === 'v' && (
            <span className="text-amber-400/80"> V scores reflect the methodology at each backtest vintage — the current 60%/40% peer-z/absolute architecture was introduced later; historical V is less comparable.</span>
          )}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Q / V toggle */}
        <div className="flex rounded overflow-hidden border border-[#1e293b]">
          {(['q', 'v'] as const).map(m => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={`px-3 py-1 text-[10px] font-semibold uppercase tracking-widest transition-colors ${
                metric === m
                  ? 'bg-[#1e3a5f] text-[#60a5fa]'
                  : 'text-[#475569] hover:text-[#94a3b8]'
              }`}
            >
              {m === 'q' ? 'Quality' : 'Valuation'}
            </button>
          ))}
        </div>

        {/* Bands toggle */}
        <button
          onClick={() => setShowBands(!showBands)}
          className={`px-3 py-1 text-[10px] rounded border transition-colors ${
            showBands
              ? 'border-[#1e3a5f] text-[#60a5fa] bg-[#0a1929]'
              : 'border-[#1e293b] text-[#475569] hover:text-[#94a3b8]'
          }`}
        >
          ±1σ bands
        </button>

        {/* Group pills */}
        <div className="flex flex-wrap gap-1.5 ml-2">
          {ALL_GROUPS.map(pg => (
            <button
              key={pg}
              onClick={() => toggleGroup(pg as Group)}
              className={`px-2 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border transition-colors ${
                activeGroups.has(pg as Group)
                  ? 'border-transparent text-[#0a0e14]'
                  : 'border-[#1e293b] text-[#475569] bg-transparent'
              }`}
              style={activeGroups.has(pg as Group) ? { background: GROUP_COLORS[pg] } : {}}
            >
              {GROUP_LABELS[pg]}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-xl p-4">
        <ResponsiveContainer width="100%" height={380}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis
              dataKey="label"
              tick={{ fill: '#475569', fontSize: 9 }}
              tickLine={false}
              axisLine={{ stroke: '#1e293b' }}
              interval={11}
            />
            <YAxis
              tick={{ fill: '#475569', fontSize: 9 }}
              tickLine={false}
              axisLine={{ stroke: '#1e293b' }}
              tickFormatter={v => v === 0 ? '↔ avg' : (v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2))}
              width={52}
            />
            <ReferenceLine y={0} stroke="#334155" strokeDasharray="4 2" strokeWidth={1} label={{ value: 'universe avg', position: 'insideTopRight', fontSize: 8, fill: '#475569', dy: 4 }} />
            <Tooltip content={<CustomTooltip metric={metric} />} />

            {ALL_GROUPS.filter(pg => activeGroups.has(pg as Group)).map(pg => {
              const color = GROUP_COLORS[pg];
              return [
                // ±1σ band as a pair of area-like lines (ReferenceArea not ideal for multi-series — use thin hi/lo lines)
                showBands && (
                  <Line
                    key={`${pg}_hi`}
                    type="monotone"
                    dataKey={`${pg}_hi`}
                    stroke={color}
                    strokeWidth={0}
                    dot={false}
                    strokeDasharray="2 2"
                    strokeOpacity={0.25}
                    legendType="none"
                    isAnimationActive={false}
                  />
                ),
                showBands && (
                  <Line
                    key={`${pg}_lo`}
                    type="monotone"
                    dataKey={`${pg}_lo`}
                    stroke={color}
                    strokeWidth={0}
                    dot={false}
                    strokeDasharray="2 2"
                    strokeOpacity={0.25}
                    legendType="none"
                    isAnimationActive={false}
                  />
                ),
                <Line
                  key={`${pg}_mean`}
                  type="monotone"
                  dataKey={`${pg}_mean`}
                  stroke={color}
                  strokeWidth={1.5}
                  dot={false}
                  name={GROUP_LABELS[pg]}
                  isAnimationActive={false}
                />,
              ];
            }).flat().filter(Boolean)}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
        {ALL_GROUPS.filter(pg => activeGroups.has(pg as Group)).map(pg => {
          const pts = data.groups[pg];
          const last = pts?.[pts.length - 1];
          const val = last ? (metric === 'q' ? last.qMean : last.vMean) : null;
          const s = last ? (metric === 'q' ? last.qStd : last.vStd) : null;
          return (
            <div key={pg} className="flex items-center gap-1.5 text-[9px]">
              <div className="w-2 h-[2px] rounded-full" style={{ background: GROUP_COLORS[pg] }} />
              <span className="text-[#64748b]">{GROUP_LABELS[pg]}</span>
              {val != null && (
                <span className="text-[#94a3b8] tabular-nums font-mono">
                  {val.toFixed(3)}
                  {s != null && <span className="text-[#475569]"> ±{s.toFixed(3)}</span>}
                </span>
              )}
            </div>
          );
        })}
        <span className="text-[9px] text-[#334155] ml-auto">latest: {data.asOf}</span>
      </div>

      {/* Sample-size note */}
      <div className="mt-3 text-[9px] text-[#334155]">
        Sample sizes (latest): {ALL_GROUPS.filter(pg => activeGroups.has(pg as Group)).map(pg => {
          const pts = data.groups[pg];
          const last = pts?.[pts.length - 1];
          return last ? `${GROUP_LABELS[pg]} n=${last.n}` : null;
        }).filter(Boolean).join(' · ')}
      </div>
    </div>
  );
}
